import React from 'react';

/**
 * Reusable Pagination Component
 * @param {Object} pagination - Pagination object with current_page, total_pages, total_count, has_next, has_previous
 * @param {Function} onPageChange - Callback function when page changes
 * @param {boolean} loading - Whether data is currently loading
 * @param {string} className - Additional CSS classes
 */
const Pagination = ({ 
  pagination, 
  onPageChange, 
  loading = false, 
  className = '',
  showInfo = true,
  showFirstLast = true 
}) => {
  if (!pagination) {
    return null;
  }

  const {
    current_page,
    total_pages,
    total_count,
    has_next,
    has_previous
  } = pagination;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= total_pages && newPage !== current_page && !loading) {
      onPageChange(newPage);
    }
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (total_pages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination
      const start = Math.max(1, current_page - 2);
      const end = Math.min(total_pages, current_page + 2);
      
      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < total_pages) {
        if (end < total_pages - 1) pages.push('...');
        pages.push(total_pages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={`pagination-container ${className}`}>
      {showInfo && (
        <div className="pagination-info">
          Page {current_page} of {total_pages} ({total_count} total items)
        </div>
      )}
      
      <div className="pagination-controls">
        {showFirstLast && (
          <button
            className="pagination-button"
            onClick={() => handlePageChange(1)}
            disabled={current_page === 1 || loading}
            title="First page"
          >
            ««
          </button>
        )}
        
        <button
          className="pagination-button"
          onClick={() => handlePageChange(current_page - 1)}
          disabled={!has_previous || loading}
          title="Previous page"
        >
          ‹
        </button>

        <div className="pagination-numbers">
          {pageNumbers.map((pageNum, index) => (
            pageNum === '...' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                ...
              </span>
            ) : (
              <button
                key={pageNum}
                className={`pagination-number ${pageNum === current_page ? 'active' : ''}`}
                onClick={() => handlePageChange(pageNum)}
                disabled={pageNum === current_page || loading}
              >
                {pageNum}
              </button>
            )
          ))}
        </div>

        <button
          className="pagination-button"
          onClick={() => handlePageChange(current_page + 1)}
          disabled={!has_next || loading}
          title="Next page"
        >
          ›
        </button>

        {showFirstLast && (
          <button
            className="pagination-button"
            onClick={() => handlePageChange(total_pages)}
            disabled={current_page === total_pages || loading}
            title="Last page"
          >
            »»
          </button>
        )}
      </div>
    </div>
  );
};

export default Pagination;